/**
 * EditRecipientEmailDialog — modal broker pour saisir/modifier `recipient_email`
 * d'un destinataire pending existant (Chunk 9.T.5.v3.7).
 *
 * Backend : `PATCH /v1/quote-requests/:id/recipients/:recipientId` — gating
 * Couche 2 `quote_request.update_recipient` (seed 0049 `agent_allowed=false`).
 *
 * UX (Q3 PO 2026-07-15) :
 *   - input `type="email"` avec validation stricte (submit bloqué si vide ou invalide)
 *   - libellé assureur + code affichés en lecture seule (pas de switch insurer)
 *   - Enter dans l'input → submit
 *   - Esc → close (sauf si mutation in-flight)
 *   - toast succès + parent invalidation cache
 *
 * Erreurs backend bilingues via `getApiErrorMessage` :
 *   - 400 VALIDATION_ERROR                        → bandeau red
 *   - 403 GATING_HUMAN_REQUIRED                   → bandeau red
 *   - 404 QUOTE_REQUESTS_NOT_FOUND                → bandeau red + close + parent refetch
 *   - 404 QUOTE_REQUEST_RECIPIENTS_NOT_FOUND      → bandeau red
 *   - 409 QUOTE_REQUESTS_RECIPIENTS_QR_LOCKED     → bandeau red
 *   - 409 QUOTE_REQUEST_RECIPIENTS_NOT_EDITABLE   → bandeau red
 *
 * Pattern parité `AddRecipientDialog` — même surface visuelle, focus sur
 * une seule action (édition email) sans re-création.
 */

import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiException } from "../../../lib/api";
import { getApiErrorMessage, normalizeLang } from "../../../lib/apiErrorMessage";
import { useInsurerCatalog } from "../../../lib/queries/insurers";
import { useUpdateQuoteRequestRecipient } from "../../../lib/queries/quoteRequests";
import { toast } from "../../../lib/toast";

type Props = {
  readonly quoteRequestId: string;
  readonly recipientId: string;
  readonly insurerCode: string;
  readonly currentEmail: string | null;
  readonly onClose: () => void;
};

export function EditRecipientEmailDialog({
  quoteRequestId,
  recipientId,
  insurerCode,
  currentEmail,
  onClose,
}: Props) {
  const { t, i18n } = useTranslation();
  const lang = normalizeLang(i18n.resolvedLanguage);
  const titleId = useId();
  const emailFieldId = useId();
  const cancelBtnRef = useRef<HTMLButtonElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const update = useUpdateQuoteRequestRecipient();
  const catalog = useInsurerCatalog();
  const { reset: resetMutation } = update;

  const [emailValue, setEmailValue] = useState<string>(currentEmail ?? "");

  // 9.V.2c-2b — libellé humain résolu depuis le registre dynamique du tenant
  // (« Beneva », ou « Beneva (inactif) » si désactivé). Fallback = code brut
  // (jamais une clé i18n). Lecture historique : le recipient garde son code.
  const insurerLabel = catalog.labelFor(insurerCode);

  useEffect(() => {
    resetMutation();
    setEmailValue(currentEmail ?? "");
    queueMicrotask(() => inputRef.current?.focus());
  }, [resetMutation, currentEmail]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !update.isPending) {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose, update.isPending]);

  const trimmedEmail = emailValue.trim();
  const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail);
  const canSubmit = isEmailValid && trimmedEmail !== (currentEmail ?? "").trim();

  const onConfirm = () => {
    if (!canSubmit) return;
    update.mutate(
      {
        quoteRequestId,
        recipientId,
        input: { recipient_email: trimmedEmail },
      },
      {
        onSuccess: () => {
          toast.success(t("quote_requests.recipients.edit_email_dialog.success"));
          onClose();
        },
      },
    );
  };

  const onFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    onConfirm();
  };

  const apiErrorMsg =
    update.error instanceof ApiException
      ? getApiErrorMessage(update.error, lang) || t("errors.generic")
      : update.isError
        ? t("errors.generic")
        : null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
    >
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
        onClick={() => {
          if (!update.isPending) onClose();
        }}
      />
      <div className="motion-safe:animate-fade-in relative z-10 w-full max-w-md rounded-xl bg-white shadow-xl ring-1 ring-slate-200">
        <header className="border-b border-slate-100 px-5 py-4">
          <h2 id={titleId} className="text-base font-semibold text-slate-900">
            {t("quote_requests.recipients.edit_email_dialog.title")}
          </h2>
          <p className="mt-0.5 text-xs text-slate-500">
            <span className="font-medium text-slate-700">{insurerLabel}</span>
            <span className="mx-1" aria-hidden="true">
              ·
            </span>
            <span className="font-mono text-[11px]" translate="no">
              {insurerCode}
            </span>
          </p>
        </header>

        <form onSubmit={onFormSubmit}>
          <div className="space-y-4 px-5 py-5">
            <div>
              <label
                htmlFor={emailFieldId}
                className="mb-1.5 block text-xs font-medium text-slate-700"
              >
                {t("quote_requests.recipients.edit_email_dialog.email_label")}{" "}
                <span aria-hidden="true" className="text-red-600">
                  *
                </span>
              </label>
              <input
                ref={inputRef}
                id={emailFieldId}
                type="email"
                autoComplete="email"
                inputMode="email"
                spellCheck={false}
                maxLength={254}
                required
                value={emailValue}
                onChange={(e) => setEmailValue(e.target.value)}
                disabled={update.isPending}
                placeholder={t("quote_requests.recipients.edit_email_dialog.email_placeholder")}
                className="focus-visible:border-brand-500 focus-visible:ring-brand-500 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 disabled:opacity-50"
              />
              <p className="mt-1.5 text-xs text-slate-500">
                {t("quote_requests.recipients.edit_email_dialog.email_hint")}
              </p>
            </div>

            {apiErrorMsg && (
              <div
                role="alert"
                className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
              >
                {apiErrorMsg}
              </div>
            )}
          </div>

          <footer className="flex items-center justify-end gap-2 border-t border-slate-100 px-5 py-4">
            <button
              ref={cancelBtnRef}
              type="button"
              onClick={onClose}
              disabled={update.isPending}
              className="focus-visible:ring-brand-500 touch-manipulation rounded-md px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50"
            >
              {t("common.cancel")}
            </button>
            <button
              type="submit"
              disabled={update.isPending || !canSubmit}
              aria-busy={update.isPending}
              className="bg-brand-600 hover:bg-brand-700 focus-visible:ring-brand-500 inline-flex touch-manipulation items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-300"
            >
              {update.isPending && (
                <svg
                  className="h-4 w-4 animate-spin motion-reduce:animate-none"
                  viewBox="0 0 16 16"
                  fill="none"
                  aria-hidden="true"
                >
                  <circle
                    cx="8"
                    cy="8"
                    r="6"
                    stroke="currentColor"
                    strokeWidth="2"
                    opacity="0.25"
                  />
                  <path
                    d="M14 8a6 6 0 0 1-6 6"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              )}
              {update.isPending
                ? t("quote_requests.recipients.edit_email_dialog.submitting")
                : t("quote_requests.recipients.edit_email_dialog.submit")}
            </button>
          </footer>
        </form>
      </div>
    </div>
  );
}
